<script setup lang="ts">
import { onMounted, reactive, ref } from 'vue'
import { useForm, useToast, VaForm } from 'vuestic-ui'
import { useI18n } from 'vue-i18n'
import { downloadBase64File } from '../../../services/utils'
import { onlyNumbersNoDigits } from '../../../core/helpers'
import { getContract, registerContract } from '../../../core/requests/Contracts'
import { format } from 'date-fns'
import IconPickerCalendar from '../../../components/icons-actions/IconPickerCalendar.vue'

const emit = defineEmits(['close'])

const vOnlyNumbers = onlyNumbersNoDigits

const props = defineProps({
  id: null,
  data: {
    default: null,
    type: Object,
  },
})

const { t } = useI18n()
const isLoading = ref(true)
const contractFiles = ref([])
const { isValid, validate, reset } = useForm('formRef')
const form: any = reactive({
  ...(props.id && { quotation: props.id }),
})
const { notify } = useToast()
const _quotations = ref([])
const items: any = ref([])
const contractDetail: any = ref(null)
const columns = [
  { key: 'ID', sortable: false },
  { key: 'Archivo', sortable: false },
  { key: 'Tipo', sortable: false },
  { key: 'Creado el', sortable: false },
  { key: 'Acciones', sortable: false },
]
const showConfirm = ref(false)
const fileSelected = ref(null)
const selectedTab = ref(0)

onMounted(async () => {
  //
  await getContract(props.id).then((resp: any) => {
    contractDetail.value = resp.data
    items.value = resp.data.file.map((e: any) => ({
      ID: e.id,
      Archivo: e.file_name,
      Tipo: e.file_mimetype,
      'Creado el': e.created_at && format(e.created_at, 'dd/MM/yyyy HH:MM'),
      Acciones: true,
      ...e,
    }))
  })

  isLoading.value = false
})

const doProcess = async () => {
  if (validate()) {
    isLoading.value = true
    try {
      await registerContract(form, contractFiles.value)
      notify({
        message: 'Contrato registrado correctamente',
        color: 'success',
      })
      reset()
      emit('close')
    } catch (e: any) {
      console.log(e)
    }

    isLoading.value = false
  }
}
const openSunatRuc = () => {
  window.open(
    'https://e-consultaruc.sunat.gob.pe/cl-ti-itmrconsruc/FrameCriterioBusquedaWeb.jsp',
    '_blank',
    'menubar=1,resizable=0,width=600,height=500',
  )
}
const needConfirm = (id: any) => {
  showConfirm.value = id
  fileSelected.value = id
}
</script>

<template>
  <div class="bg-blue-50">
    <div class="mb-4">
      <div class="flex justify-between align-center justify-center">
        <h1 class="h5 font-bold m-0">Detalle de Contrato</h1>
      </div>
    </div>

    <div v-if="!isLoading" class="new-customer gap-8">
      <VaForm ref="formRef" immediate class="gap-2">
        <div class="w-full bg-white p-2 mt-3 mb-4 py-5">
          <div class="gap-2">
            <div class="w-full columns-1">
              <h3 class="font-semibold uppercase text-sm text-primary">{{ contractDetail?.title }}</h3>
              <p class="pt-2 text-sm first-letter:capitalize">
                {{ contractDetail?.description }}
              </p>
            </div>

            <hr class="my-4" />

            <!--            <div>-->
            <!--              <div class="">-->
            <!--                <p class="font-semibold text-xxs text-gray-600">Duración del contrato</p>-->
            <!--                <p class="text-sm font-semibold">{{ contractDetail?.duration }}</p>-->
            <!--              </div>-->
            <!--            </div>-->

            <div class="mb-2">
              <p class="font-semibold text-xxs text-gray-600">Metodo de Pago</p>
              <p class="text-sm">Transferencia Bancaria</p>
            </div>

            <div class="flex gap-3">
              <div class="w-1/4">
                <p class="font-semibold text-xxs text-gray-600">Inicio de Contrato</p>
                <IconPickerCalendar :with-icon="false" :date="contractDetail?.start_at" />
              </div>

              <div class="w-1/4">
                <p class="font-semibold text-xxs text-gray-600">Fin de Contrato</p>
                <IconPickerCalendar :with-icon="false" :date="contractDetail?.expired_at" />
              </div>
            </div>

            <div class="flex gap-3 mt-3">
              <div class="w-1/4">
                <p class="font-semibold text-xxs text-gray-600">Fecha de Creación</p>
                <IconPickerCalendar :with-icon="false" :date="contractDetail?.created_at" />
              </div>

              <div class="w-1/4">
                <p class="font-semibold text-xxs text-gray-600">Fecha de Ejecución</p>
                <IconPickerCalendar :with-icon="false" :date="contractDetail?.effective_at" />
              </div>

              <div class="w-1/4">
                <p class="font-semibold text-xxs text-gray-600">Fecha de Pago</p>
                <IconPickerCalendar :with-icon="false" :date="contractDetail?.payment_deadline" />
              </div>
            </div>

            <div class="w-full mt-5">
              <VaTabs v-model="selectedTab">
                <template #tabs>
                  <VaTab v-for="tab in ['Archivos']" :key="tab">
                    {{ tab }}
                  </VaTab>
                </template>
              </VaTabs>

              <div v-if="!selectedTab">
                <hr />
                <div class="w-full mt-3">
                  <VaFileUpload
                    v-model="contractFiles"
                    drop-zone-text="Agregar archivos de contrato"
                    dropzone
                    upload-button-text="Seleccionar"
                    file-incorrect-message="El archivo seleccionado no es un documento PDF o Word"
                  />
                </div>

                <div class="flex flex-wrap mt-5">
                  <div v-for="item of items" class="w-1/2 p-1">
                    <VaCard
                      class="shadow m-0 h-full"
                      color="primary"
                      @click="downloadBase64File(item.base64_file, item.file_mimetype, item.file_name)"
                    >
                      <VaCardTitle>
                        <span class="text-xxs lowercase bg-white px-2 text-black py-1 rounded">{{
                          item.file_mimetype
                        }}</span>
                      </VaCardTitle>
                      <VaCardContent>
                        <p
                          class="hover:underline cursor-pointer text-xs font-semibold break-words"
                          @click="downloadBase64File(item.base64_file, item.file_mimetype, item.file_name)"
                        >
                          {{ item.Archivo }}
                        </p>
                      </VaCardContent>
                    </VaCard>
                  </div>
                </div>
              </div>

              <div v-if="selectedTab">
                <VaDataTable :items="items" :columns="columns">
                  <template #cell(Archivo)="{ rowData }">
                    <p
                      class="hover:underline cursor-pointer hover:text-primary"
                      @click="downloadBase64File(rowData.base64_file, rowData.file_mimetype, rowData.file_name)"
                    >
                      {{ rowData.Archivo }}
                    </p>
                  </template>

                  <template #cell(Acciones)="{ row }">
                    <div class="flex gap-1">
                      <VaButton icon="delete" color="danger" round @click="needConfirm(row?.rowData?.id)"></VaButton>
                    </div>
                  </template>
                </VaDataTable>
              </div>
            </div>
          </div>
        </div>
      </VaForm>
    </div>

    <div v-if="!isLoading" class="pb-3">
      <div class="flex justify-end">
        <div class="flex gap-2">
          <VaButton icon="save" color="primary" :disabled="!isValid" :loading="isLoading" @click="doProcess">
            <span class="text-xs px-1">Grabar</span>
          </VaButton>

          <VaButton
            icon="close"
            color="danger"
            :disabled="!isValid"
            :loading="isLoading"
            @click="
              () => {
                emit('close')
              }
            "
          >
            <span class="text-xs px-1">Cerrar</span>
          </VaButton>
        </div>
      </div>
    </div>

    <VaSkeletonGroup v-if="isLoading" color="#eff6ff" animation="wave" class="mb-5">
      <VaCard class="mb-5" :square="true">
        <VaCardContent>
          <VaSkeleton variant="squared" height="200px" />
        </VaCardContent>
      </VaCard>

      <VaCard :square="true">
        <VaCardContent class="flex items-center mb-3 gap-3">
          <VaSkeleton variant="squared" height="100px" />
          <VaSkeleton variant="squared" height="100px" />
          <VaSkeleton variant="squared" height="100px" />
        </VaCardContent>

        <VaCardActions class="flex">
          <VaSkeleton class="mr-2" variant="rounded" inline width="64px" height="32px" />
          <VaSkeleton variant="rounded" inline width="64px" height="32px" />
        </VaCardActions>
      </VaCard>
    </VaSkeletonGroup>
  </div>
</template>

<style lang="scss">
.new-customer {
  &-tabs {
    .va-tabs__content {
      width: 100%;
    }
  }

  .float-profile {
    position: absolute;
    top: 0;
    right: 0;
  }
}
</style>
